import { lazy, FC, Suspense, useState, useEffect } from 'react'
import { Route, Routes, Navigate, useLocation } from 'react-router-dom'
import { MasterLayout } from '../../_metronic/layout/MasterLayout'
import TopBarProgress from 'react-topbar-progress-indicator'
import { getCSSVariableValue } from '../../_metronic/assets/ts/_utils'
import { getSelectedLocation } from '../../utils'

const PrivateRoutes = () => {
  const SumPanel = lazy(() => import('../pages/SumPanel'))
  const SurveyPrint = lazy(() => import('../pages/Survey/SurveyPrint'))

  const [isExpired, setIsExpired] = useState(false)
  const [isRuijie, setIsRuijie] = useState(false)
  useEffect(() => {
    const expireControl = async () => {
      const selectedLocation = getSelectedLocation()
      if (selectedLocation) {
        new Date(selectedLocation.expire) < new Date() && setIsExpired(true)
        setIsRuijie(String(selectedLocation.type) === '2')
      }
    }
    expireControl()
  }, [])

  // Ruijie (type: 2) cihazlarda gösterilmeyecek ekranlar: kullanıcı işlemlerinden
  // hotspot/pppoe/vpn kullanıcıları canlı listeleri, ağ ayarları ve güvenlik ayarları
  const ruijieHiddenRouteGroups = [
    'hotspotUsers',
    'pppoeUsers',
    'vpnUsers',
    'vpnNetworkSettings',
    'ipSecVpn',
    'portForwarding',
    'staticIp',
    'ipList',
    'dhcpLease',
    'privilegeAction',
    'forbiddenMac',
    'filter',
  ]

  const getPage = (filePath: string) => {
    return lazy(() => import('../pages/' + filePath))
  }

  // isExpired ise kapatılacak rotalar
  const protectedRoutes = {
    // Lokasyon Ayarları
    campaigns: [
      { path: '/campaign', component: getPage('Campaign/Campaign') },
      { path: '/campaign/create', component: getPage('Campaign/CampaignCreate') },
      { path: '/campaign/:id/edit', component: getPage('Campaign/CampaignEdit') },
    ],
    promotions: [
      { path: '/promotion', component: getPage('Promotion/Promotion') },
      { path: '/promotion/create', component: getPage('Promotion/PromotionCreate') },
      { path: '/promotion/:id/edit', component: getPage('Promotion/PromotionEdit') },
      { path: '/promotion/:id', component: getPage('Promotion/PromotionCodes') },
    ],
    viewSettings: [{ path: '/view-settings', component: getPage('ViewSettings') }],
    defaultLimitations: [
      { path: '/nas-limit/:id', component: getPage('DefaultLimitations/DefaultLimitations') },
    ],
    surveys: [
      { path: '/survey', component: getPage('Survey/Survey') },
      { path: '/survey/create', component: getPage('Survey/SurveyCreate') },
      { path: '/survey/:id/edit', component: getPage('Survey/SurveyEdit') },
    ],
    // Kullanıcı İşlemleri
    hotspotUsers: [
      { path: '/nas-hotspot-active-list', component: getPage('HotspotUsers/HotspotUsers') },
    ],
    pppoeUsers: [{ path: '/nas-pppoe-active-list', component: getPage('PPPoEUsers/PPPoEUsers') }],

    awaitingApproval: [
      { path: '/nas-user-registered', component: getPage('AwaitingApproval/AwaitingApproval') },
    ],
    userGroups: [
      { path: '/nas-user-group', component: getPage('UserGroups/UserGroups') },
      { path: '/nas-user-group/create', component: getPage('UserGroups/UserGroupsCreate') },
      { path: '/nas-user-group/:id/edit', component: getPage('UserGroups/UserGroupsEdit') },
    ],
    registeredUsers: [
      { path: '/nas-users', component: getPage('RegisteredUsers/RegisteredUsers') },
      { path: '/nas-users/create', component: getPage('RegisteredUsers/RegisteredUserCreate') },
      { path: '/nas-users/:id/create', component: getPage('RegisteredUsers/RegisteredUserCreate') },
      { path: '/nas-users/:id/edit', component: getPage('RegisteredUsers/RegisteredUserEdit') },
      { path: '/nas-users/bulk-upload', component: getPage('RegisteredUsers/BulkUpload') },

    ],
    integrationUsers: [
      { path: '/nas-user-auto', component: getPage('IntegrationUsers/IntegrationUsers') },
    ],
    smsUsers: [{ path: '/nas-user-sms', component: getPage('SmsUsers/SmsUsers') }],
    vpnUsers: [
      { path: '/vpn-user', component: getPage('VpnUsers/VpnUsers') },
      { path: '/vpn-user/create', component: getPage('VpnUsers/VpnUsersCreate') },
      { path: '/vpn-user/:id/edit', component: getPage('VpnUsers/VpnUsersEdit') },
      { path: '/vpn-user/bulk-upload', component: getPage('VpnUsers/VpnUsersBulk') },

    ],
    coupon: [
      { path: '/coupon', component: getPage('Coupon/Coupon') },
      { path: '/coupon/create', component: getPage('Coupon/CouponCreate') },
      { path: '/coupon/:id/edit', component: getPage('Coupon/CouponEdit') },
    ],
    // Sms İşlemleri
    sms: [
      {
        path: '/sms-send',
        component: getPage('SendBulkSms/SendBulkSms'),
      },
      {
        path: '/sms-balance',
        component: getPage('SmsBalance/SmsBalance'),

      },
      {

        path: '/sms-settings',
        component: getPage('SmsSettings/SmsSettings'),
      },
      {
        path: '/sms-balance/success',
        component: getPage('SmsBalance/SmsSuccess')
      },
      {
        path: '/sms-balance/failed',
        component: getPage('SmsBalance/SmsFailed')
      }

    ],
    // Ağ ayarları
    vpnNetworkSettings: [
      { path: '/vpn-settings', component: getPage('VpnNetworkSettings/VpnNetworkSettings') },
    ],
    ipSecVpn: [
      { path: '/ipsec-vpn', component: getPage('IpsecVpn/IpsecVpn') },
      { path: '/ipsec-vpn/create', component: getPage('IpsecVpn/IpsecVpnCreate') },
      { path: '/ipsec-vpn/:id/edit', component: getPage('IpsecVpn/IpsecVpnEdit') },
    ],
    portForwarding: [
      { path: '/port-forwarding', component: getPage('PortForwarding/PortForwarding') },
      { path: '/port-forwarding/create', component: getPage('PortForwarding/PortForwardingCreate') },
      { path: '/port-forwarding/:id/edit', component: getPage('PortForwarding/PortForwardingEdit') },
    ],
    staticIp: [
      { path: '/static-ip', component: getPage('StaticIp/StaticIp') },
      { path: '/static-ip/create', component: getPage('StaticIp/StaticIpCreate') },
      { path: '/static-ip/:id/edit', component: getPage('StaticIp/StaticIpEdit') },
      { path: '/static-ip/bulk-upload', component: getPage('StaticIp/StaticIpBulk') },

    ],
    ipList: [
      { path: '/ip-list', component: getPage('IpList/IpList') },
      { path: '/ip-list/create', component: getPage('IpList/IpListCreate') },
      { path: '/ip-list/:id/edit', component: getPage('IpList/IpListEdit') },
    ],
    dhcpLease: [{ path: '/dhcp-lease', component: getPage('DhcpLease/DhcpLease') }],
    // Güvenlik ayarları
    privilegeAction: [
      { path: '/privilege-action', component: getPage('PrivilegeAction/PrivilegeAction') },
      {
        path: '/privilege-action/create',
        component: getPage('PrivilegeAction/PrivilegeActionCreate'),
      },
      {
        path: '/privilege-action/:id/edit',
        component: getPage('PrivilegeAction/PrivilegeActionEdit'),
      },


      { path: '/privilege-action/bulk-upload', component: getPage('PrivilegeAction/PrivilegeBulkUpload') },

    ],
    forbiddenMac: [
      { path: '/forbidden-mac', component: getPage('ForbiddenMac/ForbiddenMac') },
      { path: '/forbidden-mac/create', component: getPage('ForbiddenMac/ForbiddenMacCreate') },
      { path: '/forbidden-mac/:id/edit', component: getPage('ForbiddenMac/ForbiddenMacEdit') },
    ],
    filter: [
      { path: '/filter', component: getPage('Filter/Filter') },
      { path: '/filter/create', component: getPage('Filter/FilterCreate') },
      { path: '/filter/:id/edit', component: getPage('Filter/FilterEdit') },
    ],
    // Lokasyon logları
    locationLogs: [
      { path: '/dhcp-logs', component: getPage('DhcpLogs/DhcpLogs') },
      { path: '/port-logs', component: getPage('PortLogs/PortLogs') },
    ],
    // Lokasyon raporları
    locationReport: [
      { path: '/hourly-reports', component: getPage('HourlyReports/HourlyReports') },
      { path: '/daily-reports', component: getPage('DailyReports/DailyReports') },
      { path: '/weekly-reports', component: getPage('WeeklyReports/WeeklyReports') },
      { path: '/monthly-reports', component: getPage('MonthlyReports/MonthlyReports') },
    ],
    // Çoklu Yapılandırma
    multiConfiguration: [
      {
        path: '/multi-configuration',
        component: getPage('LocationConfiguration/LocationConfiguration'),
      },
    ],
    // Api Entegrasyonu
    apiIntegration: [
      {
        path: '/hotel-integration',
        component: getPage('HotelIntegration/HotelIntegration'),
      },
      {
        path: '/coupon-integration',
        component: getPage('CouponIntegration/CouponIntegration'),
      },
      {
        path: '/device-integration',
        component: getPage('DeviceIntegration/DeviceIntegration'),
      },
    ],

    // Destek
    support: [
      {
        path: '/live-support',
        component: getPage('LiveSupport/LiveSupport'),
      },
      {
        path: '/support',
        component: getPage('Support/Support'),
      },
      {
        path: '/support/:id',
        component: getPage('Support/SupportChat'),
      },
    ],
  }

  const routes = {
    // Lokasyonlarım
    locations: [
      { path: '/locations', component: getPage('Location/Locations') },
      { path: '/location/:id/edit', component: getPage('Location/LocationEdit') },
    ],
    // Lokasyon logları
    locationLogs: [
      { path: '/5651-logs', component: getPage('Logs5651/Logs5651') },
      { path: '/sms-logs', component: getPage('SmsLogs/SmsLogs') },
    ],
    // İstatistikler
    statistics: [
      { path: '/location-statistics', component: getPage('LocationStatistics/LocationStatistics') },
      { path: '/device-statistics', component: getPage('DeviceStatistics/DeviceStatistics') },
      { path: '/user-statistics', component: getPage('UserStatistics/UserStatistics') },
    ],
    // Api Entegrasyonu
    apiIntegration: [
      {
        path: '/statistic-integration',
        component: getPage('StatisticIntegration/StatisticIntegration'),
      },
    ],
    // Şifre değiştirme
    changePassword: [
      {
        path: '/change-password',
        component: getPage('ChangePassword/ChangePassword'),
      },
    ],
  }

  // Selected location silinirse tekrar eklemek için
  const location = useLocation()
  useEffect(() => {
    let localSelectedLocation = localStorage.getItem('selectedLocation')

    if (!localSelectedLocation) {
      const localStorageData: any = localStorage.getItem('kt-auth-react-v')
      const localStorageJSON = JSON.parse(localStorageData)
      const selectedLocation = localStorageJSON.data.locations[0]
      localStorage.setItem('selectedLocation', JSON.stringify(selectedLocation))
    }
  }, [location])

  return (
    <Routes>
      <Route
        path='/survey/print-survey-report'
        element={
          <SuspensedView>
            <SurveyPrint />
          </SuspensedView>
        }
      />
      <Route element={<MasterLayout />}>
        {/* Redirect to Home after success login/registartion */}
        <Route path='auth/*' element={<Navigate to='/home' />} />
        {/* Pages */}
        <Route
          path='/home'
          element={
            <SuspensedView>
              <SumPanel />
            </SuspensedView>
          }
        />
        {Object.values(routes).map((group) =>
          group.map(({ path, component: Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <SuspensedView>
                  <Component />
                </SuspensedView>
              }
            />
          ))
        )}
        {/* Protected route'ları döndür eğer isExpired ise /home redirect */}
        {Object.entries(protectedRoutes)
          .filter(([groupName]) => !(isRuijie && ruijieHiddenRouteGroups.includes(groupName)))
          .map(([, group]) =>
            group.map(({ path, component: Component }) =>
              isExpired ? (
                <Route
                  key={path}
                  path={path}
                  element={
                    <SuspensedView>
                      {isExpired ? <Navigate to='/home' /> : <Component />}
                    </SuspensedView>
                  }
                />
              ) : (
                <Route
                  key={path}
                  path={path}
                  element={
                    <SuspensedView>
                      <Component />
                    </SuspensedView>
                  }
                />
              )
            )
          )}

        {/* Page Not Found */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC = ({ children }) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {
      '0': baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export { PrivateRoutes }
